<template>
  <app-collapse class="mt-1">
    <app-collapse-item
      :is-visible="false"
      class="collapsible-section-bg"
      title="Transport"
    >
      <template #header>
        <span
          class="lead collapse-title"
        >
          <section-status-icon
            :has-data="hasTransportData"
            :has-errors="hasSectionErrors"
          />
          Transport
        </span>
      </template>
      <validation-observer>
        <!-- Transport Section -->
        <b-row v-if="parcel.transport_data">
          <b-col
            cols="12"
            md="3"
          >
            <validation-provider
              v-slot="{ errors }"
              name="Mode of transport at the border"
              rules="required"
            >
              <b-form-group>
                <label>
                  Mode of transport at the border <span class="text-danger">*</span>
                  <small class="text-muted">1903000000</small>
                  <feather-icon
                    v-b-tooltip.hover.top="'Mode of transport at the border'"
                    icon="InfoIcon"
                    size="14"
                    class="ml-50 text-muted"
                  />
                </label>
                <v-select
                  v-model="parcel.transport_data.border_transport_means_mode"
                  :options="transportModeOptions"
                  :disabled="disabled"
                  :state="errors.length > 0 ? false : null"
                />
                <small class="text-danger">{{ errors[0] }}</small>
              </b-form-group>
            </validation-provider>

            <validation-provider
              v-slot="{ errors }"
              name="Nationality"
            >
              <b-form-group>
                <label>
                  Nationality
                  <small class="text-muted">1908062000</small>
                  <feather-icon
                    v-b-tooltip.hover.top="'Nationality of the transport means at the border'"
                    icon="InfoIcon"
                    size="14"
                    class="ml-50 text-muted"
                  />
                </label>
                <v-select
                  v-model="parcel.transport_data.border_transport_means_registration_nationality"
                  :options="countriesOptions"
                  :reduce="option => option.value"
                  :disabled="disabled"
                  :state="errors.length > 0 ? false : null"
                />
                <small class="text-danger">{{ errors[0] }}</small>
              </b-form-group>
            </validation-provider>
          </b-col>

          <b-col
            cols="12"
            md="3"
          >
            <validation-provider
              v-slot="{ errors }"
              name="Inland mode of transport"
              rules="required"
            >
              <b-form-group>
                <label>
                  Inland mode of transport <span class="text-danger">*</span>
                  <small class="text-muted">1904000000</small>
                  <feather-icon
                    v-b-tooltip.hover.top="'Inland mode of transport'"
                    icon="InfoIcon"
                    size="14"
                    class="ml-50 text-muted"
                  />
                </label>
                <v-select
                  v-model="parcel.transport_data.arrival_transport_means_mode"
                  :options="transportModeOptions"
                  :disabled="disabled"
                  :state="errors.length > 0 ? false : null"
                />
                <small class="text-danger">{{ errors[0] }}</small>
              </b-form-group>
            </validation-provider>

            <validation-provider
              v-slot="{ errors }"
              name="Type of identification of arrival transport means"
            >
              <b-form-group>
                <label>
                  Type of identification of arrival transport means
                  <small class="text-muted">1906061000</small>
                  <feather-icon
                    v-b-tooltip.hover.top="'Type of identification for the arrival transport means'"
                    icon="InfoIcon"
                    size="14"
                    class="ml-50 text-muted"
                  />
                </label>
                <v-select
                  v-model="parcel.transport_data.arrival_transport_means_identification_type"
                  :options="identificationTypeOptions"
                  :disabled="disabled"
                  :state="errors.length > 0 ? false : null"
                />
                <small class="text-danger">{{ errors[0] }}</small>
              </b-form-group>
            </validation-provider>
          </b-col>

          <b-col
            cols="12"
            md="3"
          >
            <validation-provider
              v-slot="{ errors }"
              name="Identification number of arrival transport means"
            >
              <b-form-group>
                <label>
                  Identification number of arrival transport means
                  <small class="text-muted">1906017000</small>
                  <feather-icon
                    v-b-tooltip.hover.top="'Identification number of arrival transport means'"
                    icon="InfoIcon"
                    size="14"
                    class="ml-50 text-muted"
                  />
                </label>
                <b-form-input
                  v-model="parcel.transport_data.arrival_transport_means_identification"
                  v-b-tooltip.hover.top="getIssueMessage('transport_data.arrival_transport_means_identification')"
                  :class="getIssueClass('transport_data.arrival_transport_means_identification')"
                  :disabled="disabled"
                  :state="errors.length > 0 ? false : null"
                />
                <small class="text-danger">{{ errors[0] }}</small>
              </b-form-group>
            </validation-provider>

            <validation-provider
              v-slot="{ errors }"
              name="Country of dispatch"
            >
              <b-form-group>
                <label>
                  Country of dispatch
                  <small class="text-muted">1606000000</small>
                  <feather-icon
                    v-b-tooltip.hover.top="'Country from which goods are dispatched'"
                    icon="InfoIcon"
                    size="14"
                    class="ml-50 text-muted"
                  />
                </label>
                <v-select
                  v-model="parcel.transport_data.export_country"
                  :options="countriesOptions"
                  :reduce="option => option.value"
                  :disabled="disabled"
                  :state="errors.length > 0 ? false : null"
                />
                <small class="text-danger">{{ errors[0] }}</small>
              </b-form-group>
            </validation-provider>
          </b-col>

          <b-col
            cols="12"
            md="3"
          >
            <validation-provider
              v-slot="{ errors }"
              name="Country of destination"
            >
              <b-form-group>
                <label>
                  Country of destination
                  <small class="text-muted">1603000000</small>
                  <feather-icon
                    v-b-tooltip.hover.top="'Country of destination for the goods'"
                    icon="InfoIcon"
                    size="14"
                    class="ml-50 text-muted"
                  />
                </label>
                <v-select
                  v-model="parcel.transport_data.destination_country"
                  :options="countriesOptions"
                  :reduce="option => option.value"
                  :disabled="disabled"
                  :state="errors.length > 0 ? false : null"
                />
                <small class="text-danger">{{ errors[0] }}</small>
              </b-form-group>
            </validation-provider>

            <b-form-group>
              <label>
                Region of destination
                <small class="text-muted">1604000000</small>
              </label>
              <b-form-input
                v-model="parcel.transport_data.destination_region"
                v-b-tooltip.hover.top="getIssueMessage('transport_data.destination_region')"
                :class="getIssueClass('transport_data.destination_region')"
                :disabled="disabled"
              />
            </b-form-group>
          </b-col>
        </b-row>
      </validation-observer>
    </app-collapse-item>
  </app-collapse>
</template>

<script>
import {
  BRow,
  BCol,
  BFormGroup,
  BFormInput,
  VBTooltip,
} from 'bootstrap-vue'
import { ValidationObserver, ValidationProvider } from 'vee-validate'
import vSelect from 'vue-select'
import AppCollapse from '@core/components/app-collapse/AppCollapse.vue'
import AppCollapseItem from '@core/components/app-collapse/AppCollapseItem.vue'
import errorHighlightingMixin from '@/mixins/errorHighlightingMixin'
import SectionStatusIcon from '@/views/shipments/declarations/modals/components/h1/SectionStatusIcon.vue'

export default {
  components: {
    BRow,
    BCol,
    BFormGroup,
    BFormInput,
    ValidationObserver,
    ValidationProvider,
    vSelect,
    AppCollapse,
    AppCollapseItem,
    SectionStatusIcon,
  },
  directives: {
    'b-tooltip': VBTooltip,
  },
  mixins: [errorHighlightingMixin],
  props: {
    parcel: {
      type: Object,
      required: true,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    countriesOptions: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    return {
      transportModeOptions: [
        { label: 'Maritime transport', value: '1' },
        { label: 'Rail transport', value: '2' },
        { label: 'Road transport', value: '3' },
        { label: 'Air transport', value: '4' },
        { label: 'Postal consignment', value: '5' },
        { label: 'Multimodal transport', value: '7' },
        { label: 'Inland waterway transport', value: '8' },
        { label: 'Transport mode unknown', value: '9' },
      ],
      identificationTypeOptions: [
        { label: 'IMO Ship Identification Number', value: '10' },
        { label: 'Name of the vessel', value: '11' },
        { label: 'Registration number of road vehicle', value: '30' },
        { label: 'Registration number of rail wagon', value: '20' },
        { label: 'Aircraft registration mark', value: '40' },
        { label: 'IATA flight number', value: '41' },
      ],
    }
  },
  computed: {
    hasTransportData() {
      if (!this.parcel || !this.parcel.transport_data) {
        return false
      }
      const {
        border_transport_means_mode: borderTransportMeansMode,
        border_transport_means_registration_nationality: borderTransportMeansRegistrationNationality,
        arrival_transport_means_mode: arrivalTransportMeansMode,
        arrival_transport_means_identification_type: arrivalTransportMeansIdentificationType,
        arrival_transport_means_identification: arrivalTransportMeansIdentification,
        export_country: exportCountry,
        destination_country: destinationCountry,
        destination_region: destinationRegion,
      } = this.parcel.transport_data
      return !!(
        borderTransportMeansMode
        || borderTransportMeansRegistrationNationality
        || arrivalTransportMeansMode
        || arrivalTransportMeansIdentificationType
        || arrivalTransportMeansIdentification
        || exportCountry
        || destinationCountry
        || destinationRegion
      )
    },
    hasSectionErrors() {
      return Object.keys(this.issuesMap).some(key => key.startsWith('transport_data.'))
    },
  },
}
</script>

<style scoped lang="scss">
@import '@core/scss/base/bootstrap-extended/variables';
@import '@core/scss/base/core/mixins/declarations-mixins';

.collapsible-section-bg {
  @include collapsible-section-bg;
}

.input-warning {
  @include input-warning;
}

.input-danger {
  @include input-danger;
}
</style>
